import { useState, type ReactNode } from 'react'
import { format } from 'date-fns'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import {
  ArrowRightLeft, Bell, ChevronDown, ClipboardCheck, FileSearch, Gavel, Globe, Inbox, Mail,
  MessageSquare, Pencil, Phone, PhoneCall, PhoneIncoming, PhoneMissed, PhoneOutgoing, Sparkles, Undo2, Wallet,
} from 'lucide-react'

import { dateFnsLocale } from '@/utils/dateLocale'
import { getMangoCallDetail } from '@/api/inspection-leads'
import { callAttemptLabel, callResultLabel } from '@/utils/callResult'
import type { CallResult } from '@/types/inspection-lead'
import type { LeadActivityItem } from '@/api/lead-activity'

// Подписи активности — RU (операторский раздел). Имена статусов/полей берём
// из существующих ключей (leads.status.* / leads.field.*), чтобы совпадали с UI.
// Цвета иконок — в гамме «Коммуникаций» (зелёный/синий/фиолетовый/красный/янтарь).
const C = {
  teal: 'text-[color:var(--stryzh-color-accent-carlink)]',
  amber: 'text-[color:var(--stryzh-color-accent-orange)]',
  red: 'text-red-400',
  green: 'text-emerald-400',
  blue: 'text-blue-300',
  purple: 'text-purple-300',
  gray: 'text-[color:var(--stryzh-color-text-secondary)]',
}
const ic = (color: string) => `shrink-0 mt-0.5 ${color}`

function fmt(at: string): string {
  try {
    return format(new Date(at), 'd MMM HH:mm', { locale: dateFnsLocale() })
  } catch {
    return at
  }
}

function dur(sec: unknown): string {
  const s = typeof sec === 'number' ? sec : 0
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
}

function trField(f: string, t: (k: string) => string): string {
  return i18n.exists(`leads.field.${f}`) ? t(`leads.field.${f}`) : f
}
function trStatus(v: unknown, t: (k: string) => string): string {
  const s = String(v ?? '')
  return i18n.exists(`leads.status.${s}`) ? t(`leads.status.${s}`) : s
}

// Подпись источника правки. Для ручных правок (диспетчер/админ) источник не
// показываем — там виден автор (actor_name). Для автоматических — помечаем.
function fieldSrcLabel(source: string): string {
  switch (source) {
    case 'autoteka':
      return 'Автотека'
    case 'landing_form':
      return 'форма'
    case 'avito_webhook':
      return 'Авито'
    case 'system':
      return 'система'
    default:
      return ''
  }
}

interface Described {
  icon: ReactNode
  title: string
  detail?: string
}

function describeEvent(m: Record<string, unknown>): Described {
  const type = String(m.type ?? '')
  const d = (m.detail ?? {}) as Record<string, unknown>
  switch (type) {
    case 'autoteka_preview_requested':
      return { icon: <FileSearch size={16} className={ic(C.teal)} aria-hidden />, title: 'Автотека: запрос превью' }
    case 'autoteka_preview_received': {
      const car = [d.brand, d.model, d.year].filter(Boolean).join(' ')
      return { icon: <FileSearch size={16} className={ic(C.teal)} aria-hidden />, title: 'Автотека: превью', detail: car || undefined }
    }
    case 'autoteka_report_requested':
      return { icon: <FileSearch size={16} className={ic(C.teal)} aria-hidden />, title: 'Автотека: запрос отчёта' }
    case 'autoteka_report_received': {
      const got = [d.vin ? 'VIN' : null, d.regNumber ? 'госномер' : null, d.report ? 'ссылка' : null].filter(Boolean).join(', ')
      return { icon: <FileSearch size={16} className={ic(C.teal)} aria-hidden />, title: 'Автотека: отчёт получен', detail: got || undefined }
    }
    case 'autoteka_report_partial':
      return { icon: <FileSearch size={16} className={ic(C.amber)} aria-hidden />, title: 'Автотека: отчёт частично (без VIN)' }
    case 'autoteka_failed':
      return { icon: <FileSearch size={16} className={ic(C.red)} aria-hidden />, title: 'Автотека: ошибка' }
    case 'form_opened':
      return { icon: <Globe size={16} className={ic(C.gray)} aria-hidden />, title: 'Клиент открыл форму' }
    case 'form_submitted':
      return { icon: <Globe size={16} className={ic(C.gray)} aria-hidden />, title: 'Клиент отправил форму' }
    case 'status_page_viewed':
      return { icon: <Globe size={16} className={ic(C.gray)} aria-hidden />, title: 'Клиент открыл статус-страницу' }
    case 'payment_started':
      return { icon: <Wallet size={16} className={ic(C.amber)} aria-hidden />, title: 'Клиент начал оплату' }
    case 'payment_completed':
      return { icon: <Wallet size={16} className={ic(C.green)} aria-hidden />, title: 'Оплачено' }
    case 'payment_refunded':
      return { icon: <Undo2 size={16} className={ic(C.amber)} aria-hidden />, title: 'Возврат оплаты' }
    default:
      return { icon: <Globe size={16} className={ic(C.gray)} aria-hidden />, title: type || 'Событие' }
  }
}

// Торги Carlink (carlink_events): лот найден, смена статуса, протокол.
function describeAuction(m: Record<string, unknown>, t: (k: string) => string): Described {
  const status = String(m.status ?? '')
  const statusLabel = i18n.exists(`carlink.status.${status}`) ? t(`carlink.status.${status}`) : status
  const lot = m.id_auction ? `Лот ${m.id_auction}` : undefined
  const color = status === 'FAILED' ? C.red : status === 'SUCCESS' || status === 'BUYNOW' ? C.green : C.teal
  switch (String(m.event ?? '')) {
    case 'protocol':
      return { icon: <Gavel size={16} className={ic(C.teal)} aria-hidden />, title: 'Торги: получен протокол (PDF)', detail: lot }
    case 'lot_found':
      return { icon: <Gavel size={16} className={ic(color)} aria-hidden />, title: `Торги: лот найден на Carlink · ${statusLabel}`, detail: lot }
    default: {
      const relisted = m.prev_id_auction ? `перевыставлен, был лот ${m.prev_id_auction}` : null
      return {
        icon: <Gavel size={16} className={ic(color)} aria-hidden />,
        title: `Торги: ${statusLabel || String(m.event ?? '')}`,
        detail: [lot, relisted].filter(Boolean).join(' · ') || undefined,
      }
    }
  }
}

function describe(item: LeadActivityItem, t: (k: string) => string): Described {
  const m = item.meta ?? {}
  switch (item.kind) {
    case 'created': {
      const src = String(m.source ?? '').toLowerCase()
      const label = src.includes('avito') ? 'Авито' : src === 'landingdirect' ? 'лендинг' : src === 'manual' ? 'вручную' : src === 'sberauto' ? 'СберАвто' : (m.source as string) || '—'
      return { icon: <Inbox size={16} className={ic(C.green)} aria-hidden />, title: 'Заявка создана', detail: `Источник: ${label}` }
    }
    case 'status':
      return { icon: <ArrowRightLeft size={16} className={ic(C.blue)} aria-hidden />, title: `Статус: ${trStatus(m.from, t)} → ${trStatus(m.to, t)}` }
    case 'field_change': {
      const fields = ((m.fields as string[]) ?? []).map((f) => trField(f, t))
      const src = fieldSrcLabel(String(m.source ?? ''))
      return {
        icon: <Pencil size={16} className={ic(C.gray)} aria-hidden />,
        title: src ? `Изменены поля · ${src}` : 'Изменены поля',
        detail: fields.join(', '),
      }
    }
    case 'comment': {
      const sys = m.kind === 'system'
      return {
        icon: sys ? <Sparkles size={16} className={ic(C.amber)} aria-hidden /> : <MessageSquare size={16} className={ic(C.gray)} aria-hidden />,
        title: sys ? 'Carlink AI' : 'Комментарий',
        detail: String(m.body ?? '') || undefined,
      }
    }
    case 'reminder': {
      const k = String(m.kind ?? '')
      const title = k === 'sent' ? 'Напоминание отправлено' : k === 'cancelled' ? 'Напоминание снято' : 'Напоминание установлено'
      const txt = String(m.text ?? '').trim()
      let detail: string | undefined
      if (k === 'set') {
        const when = m.fire_at ? `сработает ${fmt(String(m.fire_at))}` : ''
        detail = [when, txt].filter(Boolean).join(' — ') || undefined
      } else {
        detail = txt || undefined
      }
      return { icon: <Bell size={16} className={ic(C.amber)} aria-hidden />, title, detail }
    }
    case 'sms': {
      const src = String(m.source ?? '')
      const who = src === 'manual' ? 'вручную' : src === 'other' || src === '' ? 'прочее' : 'авто'
      const base = m.status === 'failed' ? 'SMS не отправлено' : 'SMS отправлено'
      return {
        icon: <Mail size={16} className={ic(m.status === 'failed' ? C.red : C.purple)} aria-hidden />,
        title: `${base} · ${who}`,
        detail: String(m.message ?? '') || undefined,
      }
    }
    case 'call': {
      const out = m.direction === 'outgoing'
      const missed = m.duration_sec === 0
      const icon = missed
        ? <PhoneMissed size={16} className={ic(C.red)} aria-hidden />
        : out
          ? <PhoneOutgoing size={16} className={ic(C.blue)} aria-hidden />
          : m.direction === 'incoming'
            ? <PhoneIncoming size={16} className={ic(C.green)} aria-hidden />
            : <Phone size={16} className={ic(C.gray)} aria-hidden />
      const label = missed
        ? (m.direction === 'outgoing' ? 'Недозвон (исходящий)' : 'Недозвон')
        : (out ? 'Звонок исходящий' : m.direction === 'incoming' ? 'Звонок входящий' : 'Звонок')
      const title = missed ? label : `${label}, ${dur(m.duration_sec)}`
      const sub = [
        m.manager ? String(m.manager) : null,
        m.has_recording ? 'есть запись' : null,
        m.result ? callResultLabel(m.result as CallResult, t) : null,
      ].filter(Boolean).join(' · ')
      return { icon, title, detail: sub || undefined }
    }
    case 'call_result': {
      const r = m.result as CallResult
      const parts = [
        m.attempt != null ? callAttemptLabel(Number(m.attempt), t) : null,
        m.callback_at ? `перезвонить ${fmt(String(m.callback_at))}` : null,
        m.note ? String(m.note) : null,
        !item.actor_id ? 'автоматически по звонку' : null,
      ].filter(Boolean)
      return {
        icon: <PhoneCall size={16} className={ic(r === 'no_answer' || r === 'unreachable' ? C.amber : C.green)} aria-hidden />,
        title: `Результат звонка: ${callResultLabel(r, t)}`,
        detail: parts.join(' · ') || undefined,
      }
    }
    case 'order':
      return { icon: <ClipboardCheck size={16} className={ic(C.green)} aria-hidden />, title: 'Создан заказ (осмотр)' }
    case 'event':
      return describeEvent(m)
    case 'auction':
      return describeAuction(m, t)
    default:
      return { icon: <Globe size={16} className={ic(C.gray)} aria-hidden />, title: item.kind }
  }
}

// Развёрнутое содержимое строки (аккордеон). Возвращает null, если у события
// нет дополнительных подробностей — тогда строка не раскрывается.
function ExpandedContent({ item, t }: { item: LeadActivityItem; t: (k: string) => string }) {
  const m = item.meta ?? {}
  switch (item.kind) {
    case 'call':
      return <CallDetail callId={String(m.call_id ?? '')} />
    case 'field_change': {
      const changes = (m.changes as { field: string; old: unknown; new: unknown }[]) ?? []
      if (changes.length === 0) return null
      return (
        <dl className="space-y-1.5">
          {changes.map((ch, i) => (
            <div key={i} className="text-[length:var(--stryzh-font-size-xs)]">
              <dt className="text-[color:var(--stryzh-color-text-secondary)]">{trField(ch.field, t)}</dt>
              <dd className="flex flex-wrap items-center gap-1 text-[color:var(--stryzh-color-text-primary)]">
                <span className="break-all rounded bg-[rgba(255,255,255,0.05)] px-1.5 py-0.5">{fmtVal(ch.old)}</span>
                <ArrowRightLeft size={11} className="shrink-0 text-[color:var(--stryzh-color-text-secondary)]" aria-hidden />
                <span className="break-all rounded bg-[rgba(255,255,255,0.08)] px-1.5 py-0.5">{fmtVal(ch.new)}</span>
              </dd>
            </div>
          ))}
        </dl>
      )
    }
    case 'status': {
      const src = String(m.source ?? '')
      return (
        <div className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {trStatus(m.from, t)} → {trStatus(m.to, t)}
          {src ? <span> · источник: {src}</span> : null}
        </div>
      )
    }
    case 'sms': {
      const src = String(m.source ?? '') || 'other'
      const srcKey = `leads.communications.smsSource.${src}`
      const srcLabel = i18n.exists(srcKey) ? t(srcKey) : src
      return (
        <div className="space-y-1.5">
          <div className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">{srcLabel}</div>
          <div className="whitespace-pre-wrap rounded bg-[rgba(255,255,255,0.05)] px-2.5 py-1.5 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-primary)]">
            {String(m.message ?? '') || '—'}
          </div>
        </div>
      )
    }
    case 'comment':
      return (
        <div className="whitespace-pre-wrap rounded bg-[rgba(255,255,255,0.05)] px-2.5 py-1.5 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-primary)]">
          {String(m.body ?? '') || '—'}
        </div>
      )
    case 'reminder': {
      const text = String(m.text ?? '')
      const fire = m.fire_at ? `сработает ${fmt(String(m.fire_at))}` : null
      if (!text && !fire) return null
      return (
        <div className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {fire ? <div>{fire}</div> : null}
          {text ? <div className="mt-0.5 text-[color:var(--stryzh-color-text-primary)]">{text}</div> : null}
        </div>
      )
    }
    case 'event': {
      const d = (m.detail ?? {}) as Record<string, unknown>
      const entries = Object.entries(d).filter(([, v]) => v !== null && v !== '' && v !== undefined)
      if (entries.length === 0) return null
      return (
        <dl className="space-y-0.5 text-[length:var(--stryzh-font-size-xs)]">
          {entries.map(([k, v]) => (
            <div key={k} className="flex gap-1.5">
              <dt className="text-[color:var(--stryzh-color-text-secondary)]">{k}:</dt>
              <dd className="break-all text-[color:var(--stryzh-color-text-primary)]">{fmtVal(v)}</dd>
            </div>
          ))}
        </dl>
      )
    }
    default:
      return null
  }
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'да' : 'нет'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

// true, если у события есть что раскрыть.
function isExpandable(item: LeadActivityItem): boolean {
  const m = item.meta ?? {}
  switch (item.kind) {
    case 'call':
      return Boolean(m.call_id)
    case 'field_change':
      return ((m.changes as unknown[]) ?? []).length > 0
    case 'status':
      return true
    case 'sms':
      return Boolean(m.message)
    case 'comment': {
      // Раскрываем только длинные комментарии (AI-саммари). Короткие эхо
      // (напр. «Напомню 21.06 — позвонить») и так видны целиком в строке.
      const body = String(m.body ?? '')
      return body.length > 120 || body.includes('\n')
    }
    case 'reminder':
      // Напоминание видно целиком в строке (время + текст) — раскрывать нечего.
      return false
    case 'event':
      return Object.values((m.detail ?? {}) as Record<string, unknown>)
        .some((v) => v !== null && v !== '' && v !== undefined)
    default:
      return false
  }
}

function CallDetail({ callId }: { callId: string }) {
  const { t } = useTranslation()
  const detailQ = useQuery({
    queryKey: ['mango-call-detail', callId],
    queryFn: () => getMangoCallDetail(callId),
    enabled: Boolean(callId),
  })

  if (!callId) return null
  if (detailQ.isLoading) {
    return <p className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">{t('leads.calls.loadingDetail')}</p>
  }
  if (detailQ.isError) {
    return <p className="text-[length:var(--stryzh-font-size-xs)] text-red-400">{t('leads.calls.detailError')}</p>
  }
  const phrases = detailQ.data?.transcript ?? []
  return (
    <div className="flex flex-col gap-2">
      {detailQ.data?.recordingUrl ? (
        <audio controls preload="none" src={detailQ.data.recordingUrl} className="w-full">
          {t('leads.calls.audioUnsupported')}
        </audio>
      ) : (
        <p className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">{t('leads.calls.recordingUnavailable')}</p>
      )}
      {phrases.length === 0 ? (
        <p className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">{t('leads.calls.noTranscript')}</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {phrases.map((p, i) => (
            <div
              key={i}
              className={
                p.role === 'agent'
                  ? 'self-end max-w-[85%] rounded-lg bg-blue-500/15 px-3 py-1.5 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)]'
                  : 'self-start max-w-[85%] rounded-lg bg-[rgba(255,255,255,0.06)] px-3 py-1.5 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)]'
              }
            >
              <span className="mb-0.5 block text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                {p.role === 'agent' ? t('leads.calls.manager') : t('leads.calls.client')}
              </span>
              {p.text}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function LeadActivityRow({ item }: { item: LeadActivityItem }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const d = describe(item, t)
  const expandable = isExpandable(item)

  return (
    <li className="rounded border border-[color:var(--stryzh-color-border-subtle)] bg-[color:var(--stryzh-color-void)]">
      <div
        className={`flex gap-2 px-3 py-2${expandable ? ' cursor-pointer select-none' : ''}`}
        onClick={expandable ? () => setOpen((v) => !v) : undefined}
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        onKeyDown={expandable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v) } } : undefined}
      >
        {d.icon}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[length:var(--stryzh-font-size-sm)] font-medium text-[color:var(--stryzh-color-text-primary)]">
              {d.title}
            </span>
            <span className="flex shrink-0 items-center gap-1">
              <span className="font-[family-name:var(--stryzh-font-family-mono)] text-[10px] text-[color:var(--stryzh-color-text-secondary)]">
                {fmt(item.at)}
              </span>
              {expandable ? (
                <ChevronDown
                  size={13}
                  className={`text-[color:var(--stryzh-color-text-secondary)] transition-transform${open ? ' rotate-180' : ''}`}
                  aria-hidden
                />
              ) : null}
            </span>
          </div>
          {d.detail ? (
            <div className="mt-0.5 break-words text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)] line-clamp-2">
              {d.detail}
            </div>
          ) : null}
          {item.actor_name ? (
            <div className="mt-0.5 text-[10px] text-[color:var(--stryzh-color-text-secondary)]">{item.actor_name}</div>
          ) : null}
        </div>
      </div>
      {expandable && open ? (
        <div className="border-t border-[color:var(--stryzh-color-border-subtle)] px-3 py-2.5 pl-9">
          <ExpandedContent item={item} t={t} />
        </div>
      ) : null}
    </li>
  )
}

export function LeadActivityList({ items }: { items: LeadActivityItem[] }) {
  return (
    <ol className="space-y-2">
      {items.map((it, i) => (
        <LeadActivityRow key={`${it.kind}-${it.at}-${i}`} item={it} />
      ))}
    </ol>
  )
}
